import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { crefisaService } from "@/app/services/crefisaService";
import { GetFilesResponse } from "@/app/services/crefisaService/getFiles";
import { useToast } from "@/view/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

export interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
}

export interface UploadSlot {
  id: string;
  label: string;
  allowMultiple: boolean;
}

interface useUploadFilesModalProps {
  onContinue?: () => void;
}

const MAX_FILE_SIZE = 4 * 1024 * 1024;

const DOCUMENT_LABELS: Record<string, string> = {
  contracheque: "Contracheque",
  extratobancario: "Extrato Bancário",
  rg: "RG",
  portal_cidadao: "Portal do Cidadão",
  tela_compartilhamento: "Tela de Compartilhamento",
};

const isAcceptedFile = (type: string, name: string) =>
  type.startsWith("image/") ||
  type === "application/pdf" ||
  /\.(pdf|jpg|jpeg|png|heic|webp)$/i.test(name);

const getDocumentKey = (nomeDocumento: string): string | null => {
  const match = nomeDocumento.match(/^arquivos_(.+)\.pdf$/i);
  return match ? match[1].toLowerCase() : null;
};

const getDocumentLabel = (nomeDocumento: string): string => {
  const key = getDocumentKey(nomeDocumento);
  if (!key) return nomeDocumento;

  return DOCUMENT_LABELS[key] ?? key;
};

const getSlots = (doc: GetFilesResponse): UploadSlot[] => {
  const code = String(doc.codigoDocumento);

  if (getDocumentKey(doc.nomeDocumento) === "rg") {
    return [
      { id: `${code}-frente`, label: "RG - frente", allowMultiple: false },
      { id: `${code}-verso`, label: "RG - verso", allowMultiple: false },
    ];
  }

  return [
    {
      id: code,
      label: getDocumentLabel(doc.nomeDocumento),
      allowMultiple: false,
    },
  ];
};

const truncateFileName = (name: string, maxLength = 20) =>
  name.length <= maxLength ? name : name.slice(0, maxLength) + "...";

export default function useUploadFilesModal({
  onContinue,
}: useUploadFilesModalProps) {
  const { simulationData } = useCrefisaContext();
  const { toast } = useToast();

  const [files, setFiles] = useState<Record<string, UploadedFile[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: documents = [], isLoading: isLoadingFiles } = useQuery({
    queryKey: ["crefisa-files", simulationData!.clientId],
    queryFn: () => crefisaService.getFiles(simulationData!.clientId!),
    enabled: !!simulationData!.clientId,
  });

  const slots = documents.flatMap(getSlots);

  const handleAddFile = (slot: UploadSlot, file: File) => {
    const type = file.type || "application/octet-stream";

    if (!isAcceptedFile(type, file.name)) {
      toast({
        title: "Formato inválido",
        description: "Envie apenas imagens ou arquivos PDF.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "Arquivo muito grande",
        description: "Cada arquivo deve ter no máximo 4MB.",
        variant: "destructive",
      });
      return;
    }

    const newFile: UploadedFile = {
      id: crypto.randomUUID(),
      file,
      preview: type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
    };

    setFiles((prev) => ({
      ...prev,
      [slot.id]: slot.allowMultiple
        ? [...(prev[slot.id] ?? []), newFile]
        : [newFile],
    }));
  };

  const handleRemoveFile = (slotId: string, index: number) => {
    setFiles((prev) => ({
      ...prev,
      [slotId]: (prev[slotId] ?? []).filter((_, i) => i !== index),
    }));
  };

  const submitModal = async () => {
    setIsSubmitting(true);
    try {
      for (const doc of documents) {
        const docFiles = getSlots(doc).flatMap(
          (slot) => files[slot.id] ?? [],
        );

        if (docFiles.length === 0) continue;

        await crefisaService.uploadFiles({
          url: doc.urlDocumento,
          name: doc.nomeDocumento,
          code: String(doc.codigoDocumento),
          files: docFiles.map((f) => f.file),
        });
      }
      onContinue?.();
    } catch (error: any) {
      toast({
        title: "Erro no upload",
        description: error?.message ?? "Erro para fazer upload dos arquivos.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const allFilesSelected =
    slots.length > 0 &&
    slots.every((slot) => (files[slot.id]?.length ?? 0) > 0);

  const isButtonDisabled = isSubmitting || isLoadingFiles || !allFilesSelected;

  return {
    slots,
    files,
    handleAddFile,
    handleRemoveFile,
    submitModal,
    isSubmitting,
    isLoadingFiles,
    truncateFileName,
    isButtonDisabled,
  };
}
